import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getCreateNetworkDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/create-network.form-default-values';
import { CreateNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/create-network.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { NetworkCreateFormController } from '@app/feature/network/network-create/network-create-form/network-create-form.controller';
import { CreateNetworkContextProvider } from '@app/feature/network/network.context';
import { useCpmsBoCreateNetworkMutation } from '@service/cpms-bo-network.service';

interface NetworkCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const NetworkCreateController = ({
  onCancel,
  onSuccess,
}: NetworkCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createNetworkMutation] = useCpmsBoCreateNetworkMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<CreateNetworkModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.network-create.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<CreateNetworkModel> = (
    fieldValues,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const createNetwork = {
      ...fieldValues,
      billingDetails: {
        ...fieldValues.billingDetails,
        firstName: fieldValues.holder.firstName,
        lastName: fieldValues.holder.lastName,
        address: fieldValues.holder.address,
        mandate:
          fieldValues.billingDetails.mandate?.dateOfSignature ||
          fieldValues.billingDetails.mandate?.documentNumber
            ? {
                dateOfSignature:
                  fieldValues.billingDetails.mandate?.dateOfSignature,
                documentNumber:
                  fieldValues.billingDetails.mandate?.documentNumber,
              }
            : undefined,
      },
    };

    createNetworkMutation(createNetwork)
      .unwrap()
      .then(() => {
        toast.success(t('$.network-create.success'));
        onSuccess();
      })
      .catch((error) => {
        Logger.error(error);
        toast.error(
          t('$.network-create.error', { error: t('global.unknown') }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getCreateNetworkDefaultValues();

  return (
    <CreateNetworkContextProvider data={data}>
      <NetworkCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateNetworkContextProvider>
  );
};
